(ns practitest.frontend.summary.views
  (:require [clojure.string :as str]
            [goog.dom.fullscreen :as fullscreen]
            [re-frame.core :as re-frame]
            [reagent.core :as reagent]
            [reagent.dom :as rdom]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.format-utils :as fu]
            [practitest.frontend.common.i18n :refer [get-translation]]
            [practitest.frontend.components.grid.columns-settings :as oldsettings]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.overflow :as overflow]
            [practitest.frontend.components.selector :as selector]
            [practitest.frontend.components.tooltip :as tooltip]
            [practitest.frontend.components2.buttons :as buttons]
            [practitest.frontend.components2.dashboard.card :as dashboard-card]
            [practitest.frontend.components2.dashboard.chart :as dashboard-chart]
            [practitest.frontend.components2.dashboard.dashboard-tab-menu :as dashboard-tab-menu]
            [practitest.frontend.components2.dashboard.dashboard-tab-modal :as dashboard-tab-modal]
            [practitest.frontend.components2.dashboard.helpers :as dashboard-helpers]
            [practitest.frontend.components2.dashboard.tab-events :as tab-events]
            [practitest.frontend.components2.dndkit :as dndkit]
            [practitest.frontend.components2.dropdown :as dropdown]
            [practitest.frontend.components2.horizontal-scroll-strip :as hss]
            [practitest.frontend.components2.search-box :as search-box]
            [practitest.frontend.components2.spinners :as spinners]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.subs :as app-subs]
            [practitest.frontend.summary.events :as events]
            [practitest.frontend.summary.resize :as resize]
            [practitest.frontend.summary.subs :as subs]))

;; A drag starts only after moving this many px, so clicks on draggable elements still work
(def ^:private drag-distance 5)

;; The .summary-page element while it's in browser fullscreen, else nil (kept in sync by `page`)
(defonce ^:private fullscreen-el (reagent/atom nil))

(defn- on-drag-end-fn
  "dnd-kit onDragEnd handler that moves `active` to `over` in `ids` and calls on-reorder."
  [ids on-reorder]
  (fn [e]
    (let [active (some-> e .-active .-id)
          over   (some-> e .-over .-id)]
      (when (and active over (not= active over))
        (on-reorder (oldsettings/vector-move ids (.indexOf ids active) (.indexOf ids over)))))))

(defn- sortable-tab [{:keys [tab active? can-sort?]}]
  (let [{:keys [id name]} tab
        {:keys [setNodeRef listeners transition transform]} (dndkit/use-sortable id {})]
    [:div.tab.summary-tab
     (merge (when can-sort? listeners)
            {:ref   setNodeRef
             :class (when active? "active")
             :style {:transition transition :transform (dndkit/build-transform-string transform)}})
     [overflow/overflow-with-mux-tooltip
      {:position "bottom"}
      (fn [props]
        [:a.summary-tab-name (merge props {:on-click #(re-frame/dispatch [::events/select-tab id])})
         name])
      name]
     (when active?
       [dashboard-tab-menu/dashboard-tab-menu
        {:tab              tab
         :placement        "bottom-start"
         :on-edit          #(re-frame/dispatch [::tab-events/open-edit-modal events/db-key id])
         :on-clone         #(re-frame/dispatch [::tab-events/clone-tab id
                                                {:on-success [::events/tab-cloned]
                                                 :on-failure [::events/request-failed]}])
         :on-delete       #(re-frame/dispatch [::tab-events/delete-tab id
                                                {:on-success [::events/tab-removed id
                                                              (get-translation "dashboards.notify.deleted"
                                                                               "The dashboard tab was deleted.")]
                                                 :on-failure [::events/request-failed]}])}])]))

(defn- tabs-strip [tabs current-id can-sort?]
  (let [sensors (dndkit/use-pointer-sensors drag-distance)
        ids     (mapv :id tabs)]
    [dndkit/dnd-context
     {:sensors     sensors
      :modifiers   dndkit/horizontal-axis-modifiers
      :on-drag-end (on-drag-end-fn ids #(re-frame/dispatch [::events/sort-tabs %]))}
     [dndkit/sortable-context
      {:items    ids
       :strategy dndkit/horizontalListSortingStrategy}
      [hss/horizontal-scroll-strip {:active-key current-id}
       (into [:div.tabs-container]
             (for [tab tabs]
               ^{:key (:id tab)}
               [:f> sortable-tab {:tab       tab
                                  :active?   (= (:id tab) current-id)
                                  :can-sort? can-sort?}]))]]]))

;; "All" dropdown: search and jump to any tab, or drag rows to reorder
(defn- sortable-all-row [{:keys [tab active? can-sort? dragged?]}]
  (let [{:keys [id name]} tab
        {:keys [setNodeRef listeners transition transform isDragging]} (dndkit/use-sortable id {})]
    ;; dropdown-menu-item takes no ref/style/listeners, so the sortable wrapper carries the drag
    [:div.summary-all-tabs-row
     (merge (when can-sort? listeners)
            {:ref   setNodeRef
             :class [(when active? "active") (when can-sort? "draggable") (when isDragging "dragging")]
             :style {:transition transition :transform (dndkit/build-transform-string transform)}})
     [dropdown/dropdown-menu-item
      {:icon     [:<>
                  (when can-sort?
                    [icons/mux-drag-handle2])
                  [icons/mux-dashboard-tab]]
       :text     [overflow/overflow-with-mux-tooltip
                  {:position "right"}
                  (fn [props]
                    [:span.summary-all-tabs-name props name])
                  name]
       ;; Dropping a row fires a click on it - don't select the tab or close the menu then
       :on-click (fn [e]
                   (if @dragged?
                     (.stopPropagation e)
                     (re-frame/dispatch [::events/select-tab id])))}]]))

(defn- all-tabs-list [tabs current-id can-sort? dragged?]
  (let [sensors   (dndkit/use-pointer-sensors drag-distance)
        ids       (mapv :id tabs)
        drag-done #(js/setTimeout (fn [] (reset! dragged? false)) 0)]
    [dndkit/dnd-context
     {:sensors        sensors
      :on-drag-start  #(reset! dragged? true)
      :on-drag-cancel drag-done
      :on-drag-end    (fn [e]
                        ((on-drag-end-fn ids #(re-frame/dispatch [::events/sort-tabs %])) e)
                        (drag-done))}
     [dndkit/sortable-context
      {:items    ids
       :strategy dndkit/verticalListSortingStrategy}
      (into [:div.summary-all-tabs-list]
            (for [tab tabs]
              ^{:key (:id tab)}
              [:f> sortable-all-row {:tab       tab
                                     :active?   (= (:id tab) current-id)
                                     :can-sort? can-sort?
                                     :dragged?  dragged?}]))]]))

;; can-manage? - same permission as the Dashboard Tab List page (dashboard_private)
(defn- all-tabs-menu [tabs current-id can-manage? can-sort?]
  (reagent/with-let [search   (reagent/atom "")
                     dragged? (reagent/atom false)]
    (let [q          @search
          searching? (not (str/blank? q))
          rows       (cond->> tabs
                       searching? (filter #(fu/substring-search? (:name %) q)))
          project-id @(re-frame/subscribe [::app-subs/current-project-id])]
      [:div.m-dropdown-menu-body.summary-all-tabs
       [:div.summary-all-tabs-search
        [search-box/search-textbox
         {:placeholder    (get-translation "summary.tabs.search" "Search tabs")
          :value          q
          :on-change      #(reset! search (dom/event-value %))
          :on-clear-click #(reset! search "")}]]
       (if (seq rows)
         ;; Reordering a filtered subset is ambiguous - drag only on the full list
         [:f> all-tabs-list rows current-id (and can-sort? (not searching?)) dragged?]
         [:div.summary-all-tabs-empty (get-translation "summary.tabs.no-results" "No tabs found")])
       [:div.summary-all-tabs-footer
        [dropdown/dropdown-menu-item
         {:text      (get-translation "summary.tabs.settings" "Dashboard Tabs Settings")
          :icon      [icons/mux-gear-outline]
          :href      (broutes/get-url "dashboards#index" {:p_id project-id})
          :disabled? (not can-manage?)
          :tooltip   (when-not can-manage?
                       (get-translation "summary.tabs.settings.no-permission"
                                        "You don't have permissions to manage dashboard tabs"))}]]])))

(defn- tab-bar []
  (let [tabs        @(re-frame/subscribe [::subs/field :tabs])
        current-id  @(re-frame/subscribe [::subs/field :current-tab-id])
        ;; Creating tabs and the Dashboard Tab List page share the dashboard_private permission
        can-create? (:can_create @(re-frame/subscribe [::subs/field :new-tab]))
        can-sort?   (:can_sort_tabs @(re-frame/subscribe [::subs/field :permissions]))]
    [:div.summary-tab-bar
     [:f> tabs-strip tabs current-id can-sort?]
     [dropdown/dropdown-menu
      (fn [{:keys [toggle!]}]
        [buttons/button {:tertiary? true
                         :icon      [icons/mux-unordered-list]
                         :text      (get-translation "summary.tabs.all" "All")
                         :on-click  toggle!}])
      {:placement "bottom-end"}
      [all-tabs-menu tabs current-id can-create? can-sort?]]
     [buttons/button {:tertiary? true
                      :icon      [icons/mux-reg-plus]
                      :text      (get-translation "dashboards.add-tab" "Add Dashboard Tab")
                      :disabled? (not can-create?)
                      :tooltip   (when-not can-create?
                                   (get-translation "dashboards.add-tab.no-permission"
                                                    "You don't have permissions to create dashboard tabs"))
                      :on-click  #(re-frame/dispatch [::tab-events/open-new-modal events/db-key])}]]))

;; set-event - dispatched with the selected value (the external dashboard saves it elsewhere)
(defn tab-filter [set-event]
  (let [{:keys [label value options] :as tab-filter} @(re-frame/subscribe [::subs/field :tab-filter])
        selected (some #(when (= (:value %) value) %) options)]
    [:div.summary-tab-filter
     [icons/mux-tab-filter]
     [:span.summary-tab-filter-label (or label (get-translation "summary.tab-filter" "Tab Level Filter"))]
     [tooltip/mux-tooltip {:position "bottom"}
      (when-not tab-filter
        (get-translation "summary.tab-filter.not-set"
                         "No tab level filter is set for this tab. You can add one in Edit Tab."))
      (fn [props]
        [:div.summary-tab-filter-select props
         [selector/new-select
          {:items       (or options [])
           :key-fn      :value
           :label-fn    :label
           :value       selected
           :clearable?  true
           :disabled?   (nil? tab-filter)
           :placeholder (get-translation "summary.tab-filter.placeholder" "Select value")
           :on-change   #(re-frame/dispatch [set-event (:value %)])}]])]]))

(defn- toolbar []
  (let [tab         @(re-frame/subscribe [::subs/current-tab])
        permissions @(re-frame/subscribe [::subs/field :permissions])
        can-add?    (:can_add_item permissions)]
    [:div.summary-toolbar
     [tab-filter ::events/set-tab-filter]
     [:div.summary-toolbar-actions
      [dashboard-helpers/add-item-button {:editable? (:editable tab)
                                          :can-add?  can-add?
                                          :href      (:new_item_url tab)}]
      [buttons/button {:icon     [icons/mux-refresh]
                       :tooltip  (get-translation "generic.button.refresh" "Refresh")
                       :on-click #(re-frame/dispatch [::events/reload-items])}]
      [buttons/button {:icon     (if @fullscreen-el [icons/mux-collapse] [icons/mux-fullscreen])
                       :tooltip  (if @fullscreen-el
                                   (get-translation "summary.fullscreen.exit" "Exit full screen")
                                   (get-translation "summary.fullscreen" "Full screen"))
                       :on-click #(if @fullscreen-el
                                    (fullscreen/exitFullScreen)
                                    (fullscreen/requestFullScreen (.querySelector js/document ".summary-page")))}]]]))

;; 3-dots menu option to switch to the next width (another way to resize than dragging the edge)
(defn- width-menu-items [{:keys [id width height width_resizable]}]
  (when width_resizable
    (let [[next-width text] (case width
                              "double" ["triple" (get-translation "summary.item.width.triple" "Resize to full width")]
                              "triple" ["single" (get-translation "summary.item.width.single" "Resize to 1 column")]
                              ["double" (get-translation "summary.item.width.double" "Resize to 2 columns")])]
      [{:key  :resize
        :text text
        :evt  [::events/resize-item id {:width next-width :height height}]}])))

;; Loads its chart the first time it scrolls into view (see events/item-visible)
(defn- card-body [_item]
  (let [observer (atom nil)]
    (reagent/create-class
      {:display-name "summary-card-body"
       :component-did-mount
       (fn [this]
         (let [obs (js/IntersectionObserver.
                     (fn [entries]
                       (when (some #(.-isIntersecting %) entries)
                         (re-frame/dispatch [::events/item-visible (second (reagent/argv this))])))
                     #js {:rootMargin "200px"})]
           (reset! observer obs)
           (.observe obs (rdom/dom-node this))))
       :component-will-unmount
       (fn [_]
         (when-let [o @observer] (.disconnect o)))
       :reagent-render
       (fn [{:keys [id title editable edit_url] :as item}]
         (let [status @(re-frame/subscribe [::subs/item-status id])]
           [:div.summary-card-body
            (case status
              :loaded [dashboard-chart/chart #:chart{:id                    id
                                                     :edit-url              edit_url
                                                     :edit-disabled-tooltip (when-not editable (dashboard-helpers/no-permission-tooltip))
                                                     :extra-menu-items      (width-menu-items item)}]

              :error  [dashboard-card/card {:title title}
                       [:div.summary-card-error
                        [:span (get-translation "summary.item.load-error" "Failed to load this dashboard item.")]
                        [buttons/button {:tertiary? true
                                         :text      (get-translation "generic.button.retry" "Retry")
                                         :on-click  #(re-frame/dispatch [::events/load-item item])}]]]
              [dashboard-card/card {:title title}
               [:div.summary-card-loading [spinners/mux-spinner]]])]))})))

;; Presses starting on these keep their own behavior (links, buttons, inputs, scrollable chart area, resize handles)
(def ^:private no-drag-selector "a, button, input, select, textarea, .m-icon-button, .chart-scroll, .summary-card-resize")

(defn- sortable-card [{:keys [item can-sort?]}]
  (let [{:keys [id width height]} item
        {:keys [setNodeRef listeners transition transform isDragging]} (dndkit/use-sortable id {})
        on-pointer-down (:onPointerDown listeners)]
    ;; The whole card is draggable; a drag starts only after moving a few px, so clicks still work
    [:div.summary-card
     {:ref             setNodeRef
      :class           [(when (= width "double") "double-width")
                        (when (= width "triple") "triple-width")
                        (when (= height "double") "double-height")
                        (when can-sort? "draggable")
                        (when isDragging "dragging")]
      :style           {:transition transition :transform (dndkit/build-transform-string transform)}
      :on-pointer-down (when (and can-sort? on-pointer-down)
                         (fn [e]
                           (when-not (.closest (.-target e) no-drag-selector)
                             (on-pointer-down e))))}
     [card-body item]
     [resize/handles item #(re-frame/dispatch [::events/resize-item id %])]]))

;; Read only (external dashboard) when can-sort? is false and the items aren't resizable
(defn cards-grid [items can-sort? reload-key]
  (let [sensors (dndkit/use-pointer-sensors drag-distance)
        ids     (mapv :id items)]
    [dndkit/dnd-context
     {:sensors     sensors
      :on-drag-end (on-drag-end-fn ids #(re-frame/dispatch [::events/sort-items %]))}
     [dndkit/sortable-context
      {:items    ids
       :strategy dndkit/rectSortingStrategy}
      (into [:div.summary-cards]
            (for [item items]
              ^{:key (str (:id item) "-" reload-key)}
              [:f> sortable-card {:item item :can-sort? can-sort?}]))]]))

(defn- empty-state []
  (let [tab @(re-frame/subscribe [::subs/current-tab])]
    [:div.summary-empty
     [icons/mux-empty-dashboard]
     [:strong (get-translation "summary.empty.title" "No dashboard items yet")]
     (when (:editable tab)
       [buttons/button {:primary? true
                        :icon     [icons/mux-reg-plus]
                        :text     (get-translation "dashboards.add-item" "Add Dashboard Item")
                        :href     (:new_item_url tab)}])]))

(defn- items-section []
  (let [items      @(re-frame/subscribe [::subs/field :items])
        reload-key @(re-frame/subscribe [::subs/field :reload-key])
        tab        @(re-frame/subscribe [::subs/current-tab])]
    (if (empty? items)
      [empty-state]
      ;; Items can be reordered by whoever can edit the tab
      [:f> cards-grid items (boolean (:editable tab)) reload-key])))

(defn page []
  ;; Tracks fullscreen also when it's exited with Esc / the browser's own controls
  (reagent/with-let [on-change  #(reset! fullscreen-el (when (fullscreen/isFullScreen)
                                                         (.querySelector js/document ".summary-page")))
                     event-type fullscreen/EventType.CHANGE
                     _          (.addEventListener js/document event-type on-change)]
    (let [loading?    @(re-frame/subscribe [::subs/field :loading?])
          load-error? @(re-frame/subscribe [::subs/field :load-error?])
          tabs        @(re-frame/subscribe [::subs/field :tabs])]
      [:div.summary-page
       (cond
         (and loading? (empty? tabs))
         [:div.summary-loading [spinners/mux-spinner]]

         (and load-error? (empty? tabs))
         [:div.summary-empty
          [:span (get-translation "generic.error.general" "Something went wrong. Please try again.")]
          [buttons/button {:text     (get-translation "generic.button.retry" "Retry")
                           :on-click #(re-frame/dispatch [::events/fetch nil])}]]

         :else
         [:<>
          [:div.summary-sticky-header
           [tab-bar]
           [toolbar]]
          [:div.summary-body {:class (when loading? "loading")}
           [items-section]]])
       [dashboard-tab-modal/tab-edit-modal
        {:db-key             events/db-key
         :container-ref      (when @fullscreen-el fullscreen-el)
         :on-created         [::events/tab-saved :select
                              (get-translation "dashboards.notify.created" "The dashboard tab was created.")]
         :on-saved           [::events/tab-saved nil
                              (get-translation "dashboards.notify.updated" "The dashboard tab was updated.")]
         :on-key-regenerated [::events/key-regenerated]
         :on-failure         [::events/request-failed]}]])
    (finally
      (.removeEventListener js/document event-type on-change)
      (reset! fullscreen-el nil))))
